import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FiGithub, FiExternalLink } from 'react-icons/fi';
import { projectData } from '../data/portfolioData';
import { callChatAPI } from '../services/chatService';
import {
  prefersReducedMotion,
  pageEnter,
  EASE,
} from '../utils/motion';

gsap.registerPlugin(ScrollTrigger);

// Ordered list of all project ids for prev/next navigation
// Derived from values() to guarantee stable insertion order across all engines
const ALL_IDS = Object.values(projectData).map((p) => p.id);

export default function CaseStudyPage() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const project = projectData[projectId];
    const pageRef  = useRef(null);
    const heroBgRef = useRef(null);

    const [aiSummary, setAiSummary] = useState('');
    const [isSummarizing, setIsSummarizing] = useState(false);

    const currentIdx = ALL_IDS.indexOf(projectId);
    const prevId = currentIdx > 0 ? ALL_IDS[currentIdx - 1] : null;
    const nextId = currentIdx < ALL_IDS.length - 1 ? ALL_IDS[currentIdx + 1] : null;

    // Scroll to top on route change
    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [projectId]);

    // Entrance + scroll animations
    useEffect(() => {
        if (!project) return;

        const page = pageRef.current;
        if (!page) return;

        // Page-level fade-in (no ScrollTrigger)
        if (!prefersReducedMotion()) {
            pageEnter(page, 0.05);
        }

        if (prefersReducedMotion()) return;

        const ctx = gsap.context(() => {
            // ── Hero: cinematic staggered entrance (kept — highest value)
            const tl = gsap.timeline({ defaults: { ease: EASE.strong }, delay: 0.1 });

            tl.from('.cs-back-btn', {
                opacity: 0, x: -16, duration: 0.48,
            })
            .from('.cs-hero-category', {
                opacity: 0, y: 12, duration: 0.42,
            }, '-=0.25')
            .from('.cs-hero-title', {
                opacity: 0, y: 40, scale: 0.97, duration: 0.88,
            }, '-=0.28')
            .from('.cs-hero-subtitle', {
                opacity: 0, y: 18, duration: 0.52,
            }, '-=0.42')
            .from('.cs-tech-tag', {
                opacity: 0, y: 6,
                duration: 0.38, stagger: 0.05,
                ease: EASE.gentle,
            }, '-=0.35');

            // ── Hero bg: slow parallax (kept — very lightweight, one scrub)
            if (heroBgRef.current) {
                gsap.to(heroBgRef.current, {
                    yPercent: 8,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: '.cs-hero',
                        start: 'top top',
                        end: 'bottom top',
                        scrub: 1.5,
                    },
                });
            }

            // ── Section reveals: one tween per section (label+heading+body batched)
            // Replaced: per-section timelines with 3 tweens each + individual body stagger
            gsap.utils.toArray('.cs-section').forEach((el) => {
                gsap.from(el, {
                    opacity: 0,
                    y: 24,
                    duration: 0.65,
                    ease: EASE.standard,
                    scrollTrigger: {
                        trigger: el,
                        start: 'top 88%',
                        once: true,
                    },
                });
            });

            // ── Feature + challenge items: single batched tween per group
            // Replaced: one ScrollTrigger per card/item
            const featureCards = gsap.utils.toArray('.cs-feature-card');
            if (featureCards.length) {
                gsap.from(featureCards, {
                    opacity: 0,
                    y: 22,
                    duration: 0.52,
                    stagger: 0.07,
                    ease: EASE.spring,
                    scrollTrigger: {
                        trigger: featureCards[0],
                        start: 'top 91%',
                        once: true,
                    },
                });
            }

            const challengeItems = gsap.utils.toArray('.cs-challenge-item');
            if (challengeItems.length) {
                gsap.from(challengeItems, {
                    opacity: 0,
                    x: -16,
                    duration: 0.5,
                    stagger: 0.07,
                    ease: EASE.standard,
                    scrollTrigger: {
                        trigger: challengeItems[0],
                        start: 'top 90%',
                        once: true,
                    },
                });
            }

        }, page);

        return () => ctx.revert();
    }, [project, projectId]);

    const handleAiSummary = async () => {
        setIsSummarizing(true);
        setAiSummary('');
        const goalText = project.caseStudy?.problem
            || project.goal
            || 'No goal description available.';
        const solutionText = project.caseStudy?.solution?.intro
            || project.solution
            || 'No solution description available.';
        const details = `Goal: ${goalText}. Solution: ${solutionText}`;
        try {
            const summary = await callChatAPI(
                `Summarize this project:\nTitle: ${project.title}\n${details}`,
                'You are a professional technical writer. Write a concise, impressive single paragraph.'
            );
            setAiSummary(summary || 'Could not generate summary.');
        } catch {
            setAiSummary('Sorry — something went wrong.');
        } finally {
            setIsSummarizing(false);
        }
    };

    if (!project) {
        return (
            <div className="cs-not-found">
                <h2>Project not found</h2>
                <button className="cs-back-btn" onClick={() => navigate('/')}>← Back to Portfolio</button>
            </div>
        );
    }

    const renderBody = () => {
        if (project.caseStudy) {
            const { caseStudy } = project;
            return (
                <>
                    <div className="cs-section cs-section--overview">
                        <span className="cs-section-label">Overview</span>
                        <p>{caseStudy.overview}</p>
                    </div>

                    <div className="cs-section cs-section--problem">
                        <span className="cs-section-label">The Problem</span>
                        <h3 className="cs-section-heading">What needed solving</h3>
                        <p>{caseStudy.problem}</p>
                    </div>

                    <div className="cs-section cs-section--solution">
                        <span className="cs-section-label">The Solution</span>
                        <h3 className="cs-section-heading">How I built it</h3>
                        <p className="cs-solution-intro">{caseStudy.solution.intro}</p>

                        <div className="cs-features-grid">
                            {caseStudy.solution.features.map((f) => (
                                <div className="cs-feature-card" key={f.title}>
                                    <div className="cs-feature-card-accent" />
                                    <h4>{f.title}</h4>
                                    <p>{f.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="cs-section cs-section--challenges">
                        <span className="cs-section-label">Challenges & Learnings</span>
                        <div className="cs-challenge-list">
                            {caseStudy.challenges.map((item, i) => (
                                <div className="cs-challenge-item" key={i}>
                                    <div className="cs-challenge-number">{String(i + 1).padStart(2, '0')}</div>
                                    <div className="cs-challenge-content">
                                        <p className="cs-challenge-q">{item.challenge}</p>
                                        <p className="cs-challenge-a"><strong>Solution:</strong> {item.solution}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="cs-section cs-section--outcomes">
                        <span className="cs-section-label">Outcomes</span>
                        <h3 className="cs-section-heading">Impact & results</h3>
                        <p className="cs-outcomes-text">{caseStudy.outcomes}</p>
                    </div>
                </>
            );
        }

        // Simple project
        return (
            <>
                <div className="cs-section">
                    <span className="cs-section-label">The Goal</span>
                    <p>{project.goal}</p>
                </div>
                <div className="cs-section">
                    <span className="cs-section-label">My Solution</span>
                    <p>{project.solution}</p>
                </div>
                <div className="cs-section">
                    <span className="cs-section-label">Key Features</span>
                    <div className="cs-features-grid cs-features-grid--simple">
                        {project.features?.map((f) => (
                            <div className="cs-feature-card" key={f}>
                                <div className="cs-feature-card-accent" />
                                <p>{f}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="cs-page" ref={pageRef}>
            {/* ── Hero ─────────────────────────────────── */}
            <div className="cs-hero">
                {project.imageUrl && (
                    <>
                        <div
                            className="cs-hero-bg"
                            ref={heroBgRef}
                            style={{ backgroundImage: `url(${project.imageUrl})` }}
                            aria-hidden="true"
                        />
                        <div className="cs-hero-veil" aria-hidden="true" />
                    </>
                )}
                <div className="cs-hero-inner">
                    <button className="cs-back-btn" onClick={() => navigate('/')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                            <path d="M19 12H5M12 5l-7 7 7 7" />
                        </svg>
                        Back to Portfolio
                    </button>

                    {project.category && (
                        <span className="cs-hero-category">{project.category}</span>
                    )}

                    <h1 className="cs-hero-title">{project.title}</h1>
                    <p className="cs-hero-subtitle">{project.subtitle || project.shortDescription}</p>

                    <div className="cs-hero-tech">
                        {project.techStack?.map((t) => (
                            <span key={t} className="cs-tech-tag">{t}</span>
                        ))}
                    </div>

                    {/* External links */}
                    {(project.githubUrl || project.liveUrl) && (
                        <div className="cs-hero-links">
                            {project.githubUrl && (
                                <a
                                    href={project.githubUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cs-hero-link"
                                    aria-label="View source code on GitHub"
                                >
                                    <FiGithub />
                                    <span>GitHub</span>
                                </a>
                            )}
                            {project.liveUrl && (
                                <a
                                    href={project.liveUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cs-hero-link cs-hero-link--primary"
                                    aria-label="View live demo"
                                >
                                    <FiExternalLink />
                                    <span>Live Demo</span>
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Body ─────────────────────────────────── */}
            <div className="cs-body">
                <div className="cs-container">

                    {/* AI Summary */}
                    <div className="cs-section cs-ai-section">
                        <button
                            className="cs-ai-btn"
                            onClick={handleAiSummary}
                            disabled={isSummarizing}
                        >
                            {isSummarizing
                                ? <><span className="cs-spinner" aria-hidden="true" />Generating AI Summary…</>
                                : <>✦ Generate AI Summary</>}
                        </button>
                        {aiSummary && (
                            <div className="cs-ai-result">
                                <p>{aiSummary}</p>
                            </div>
                        )}
                    </div>

                    {renderBody()}

                    {/* ── Next / Prev nav ─────────────────── */}
                    <div className="cs-nav-row">
                        {prevId ? (
                            <button className="cs-nav-btn cs-nav-btn--prev" onClick={() => navigate(`/projects/${prevId}`)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                                    <path d="M19 12H5M12 5l-7 7 7 7" />
                                </svg>
                                <span>
                                    <small>Previous</small>
                                    <strong>{projectData[prevId].title}</strong>
                                </span>
                            </button>
                        ) : <div />}

                        {nextId ? (
                            <button className="cs-nav-btn cs-nav-btn--next" onClick={() => navigate(`/projects/${nextId}`)}>
                                <span>
                                    <small>Next</small>
                                    <strong>{projectData[nextId].title}</strong>
                                </span>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                        ) : <div />}
                    </div>

                </div>
            </div>
        </div>
    );
}
